import { Button, Loader, TextInput } from '@mantine/core';
import { Search } from 'lucide-react';

import Paragraph from '@/components/text/Paragraph.tsx';

import useAuth from '@/hooks/useAuth.ts';
import usePatientSearch from '@/hooks/usePatientSearch.ts';

import { GenericPatient } from '@/models/GenericPatient.ts';
import { UserGroup } from '@/models/User.ts';

import { useWalkInStore } from '@/stores/walkInStore.ts';

import { getPatientIdFromUser, updateCurrentPatient } from '@/utils/patientUtils.ts';

import { SelectableList } from '../SelectableList.tsx';
import Heading3 from '../text/Heading3.tsx';
import PatientRow from './PatientRow.tsx';

interface Props {
    setIsPatientSelected: (value: boolean) => void;
}

export const SearchPatient = ({ setIsPatientSelected }: Props) => {
    const { isUserInGroup } = useAuth();
    const selectedInstanceId = useWalkInStore((state) => state.selected_instance_id);

    const onPatientSelect = (patient: GenericPatient | undefined): void => {
        if (patient) {
            setIsPatientSelected(true);
        } else {
            setIsPatientSelected(false);
        }
        updateCurrentPatient(patient);
    };

    const {
        data,
        is_loading,
        is_validating,
        selected_item,
        handleSelect: searchHookSelect,
        onChangeSearch,
    } = usePatientSearch(onPatientSelect, selectedInstanceId);

    const isSearchDurationVisible = isUserInGroup([
        UserGroup.DEV_INTERNAL_ENGINEERS,
        UserGroup.PROD_INTERNAL_ENGINEERS,
        UserGroup.DEV_INTERNAL_QA,
        UserGroup.PROD_INTERNAL_QA,
    ]);

    return (
        <div className="flex w-full flex-col gap-3">
            <div className="flex gap-5">
                <Heading3>{`Person finden (${data?.results_count ?? 0} von ${data?.total_count ?? 0} Ergebnissen)`}</Heading3>
                {isSearchDurationVisible && data?.duration && <p>{`${Math.round(data.duration / 10) / 100}s`}</p>}
            </div>
            <div className="flex items-center justify-stretch gap-4">
                <TextInput
                    className="w-3/4"
                    placeholder="Name, Geburtstagsdatum, Email, Patientennummer"
                    rightSectionPointerEvents="none"
                    leftSection={(is_loading || is_validating) && <Loader size="xs" />}
                    rightSection={<Search className="size-5" />}
                    onChange={(e) => onChangeSearch(e.target.value)}
                />
                <Button className="w-1/4 !p-2.5" variant="filled">
                    Suchen
                </Button>
            </div>
            <div className="h-[50vh] overflow-scroll">
                {data?.patients && data?.patients?.length > 0 ? (
                    <SelectableList handleSelect={searchHookSelect} selectedItem={selected_item}>
                        {data?.patients.map((patient) => (
                            <PatientRow
                                matches={patient.matches}
                                patient={patient.user}
                                key={getPatientIdFromUser(patient.user)}
                            />
                        ))}
                    </SelectableList>
                ) : (
                    <div className="flex h-48 w-full items-center justify-center rounded-md bg-slate-300 bg-opacity-50 dark:bg-neutral-600">
                        {is_loading && <Loader />}
                        {!is_loading && (
                            <Paragraph className="text-center text-slate-700 dark:text-neutral-300">
                                Name, Geburtsdatum, Email oder Patientennummer angeben
                            </Paragraph>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
